/**
 * RepoCard
 * Card displaying a connected repository with its analysis status and actions
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Github, Clock, AlertCircle, CheckCircle, Loader } from 'lucide-react';
import { Button, Badge } from '../../ui';
import type { ConnectedRepo } from '../../../types';
import styles from './RepoCard.module.css';

export interface RepoCardProps {
  repo: ConnectedRepo;
  onReAnalyze?: (repoId: string) => void;
  onPushToRepo?: (repoId: string) => void;
}

export const RepoCard: React.FC<RepoCardProps> = ({
  repo,
  onReAnalyze,
  onPushToRepo,
}) => {
  const navigate = useNavigate();

  // Get status badge variant
  const getStatusVariant = (): 'default' | 'success' | 'warning' | 'error' => {
    switch (repo.analysisStatus) {
      case 'complete':
        return 'success';
      case 'analyzing':
        return 'warning';
      case 'failed':
        return 'error';
      case 'outdated':
        return 'warning';
      default:
        return 'default';
    }
  };

  // Get status icon
  const getStatusIcon = () => {
    switch (repo.analysisStatus) {
      case 'complete':
        return <CheckCircle size={14} />;
      case 'analyzing':
        return <Loader size={14} className={styles.spinning} />;
      case 'failed':
        return <AlertCircle size={14} />;
      case 'outdated':
        return <Clock size={14} />;
      default:
        return null;
    }
  };

  // Format relative time
  const formatRelativeTime = (dateString?: string): string => {
    if (!dateString) return 'Never';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    return `${diffDays}d ago`;
  };

  return (
    <div className={styles.card}>
      {/* Header */}
      <div className={styles.header}>
        <div className={styles.repoName}>
          <Github size={18} />
          <a
            href={`https://github.com/${repo.fullName}`}
            target="_blank"
            rel="noopener noreferrer"
            className={styles.repoLink}
          >
            {repo.fullName}
          </a>
        </div>
        <Badge variant={getStatusVariant()} className={styles.statusBadge}>
          {getStatusIcon()}
          {(repo.analysisStatus || 'pending').replace('-', ' ')}
        </Badge>
      </div>

      {/* Tech Stack */}
      {repo.techStack && (
        <div className={styles.techStack}>
          {repo.techStack.framework && (
            <span className={styles.techBadge}>{repo.techStack.framework}</span>
          )}
          {(repo.techStack.languages || []).slice(0, 3).map((lang) => (
            <span key={lang.name} className={styles.techBadge}>
              {lang.name}
            </span>
          ))}
          {repo.techStack.database && (
            <span className={styles.techBadge}>{repo.techStack.database}</span>
          )}
        </div>
      )}

      {/* Stats */}
      <div className={styles.stats}>
        <div className={styles.stat}>
          <span className={styles.statValue}>{repo.agentCount}</span>
          <span className={styles.statLabel}>Agents</span>
        </div>
        <div className={styles.stat}>
          <span className={styles.statValue}>{repo.skillCount}</span>
          <span className={styles.statLabel}>Skills</span>
        </div>
        <div className={styles.stat}>
          <span className={styles.statLabel}>Last Synced</span>
          <span className={styles.statValue}>{formatRelativeTime(repo.lastSyncedAt)}</span>
        </div>
      </div>

      {/* Actions */}
      <div className={styles.actions}>
        <Button
          variant="secondary"
          size="sm"
          onClick={() => navigate(`/developer-agents/${encodeURIComponent(repo.id)}`)}
          disabled={repo.analysisStatus === 'not-analyzed'}
        >
          View Details
        </Button>
        <Button
          variant="secondary"
          size="sm"
          onClick={() => onReAnalyze?.(repo.id)}
          disabled={repo.analysisStatus === 'analyzing'}
        >
          Re-analyze
        </Button>
        <Button
          variant="primary"
          size="sm"
          onClick={() => onPushToRepo?.(repo.id)}
          disabled={repo.analysisStatus !== 'complete'}
        >
          Push to Repo
        </Button>
      </div>
    </div>
  );
};

export default RepoCard;
